import { useState, useEffect } from 'react';
import { CalendarEvent } from '../types/calendar';
import { useDeals } from '../store/DealsContext';
import { useZoomMeetings } from './useZoomMeetings';
import { useLocalStorage } from './useLocalStorage';

export function useCalendar() {
  const [events, setEvents] = useLocalStorage<CalendarEvent[]>('calendar_events', []);
  const [loading, setLoading] = useState(false);
  const { deals, tasks } = useDeals();
  const { meetings } = useZoomMeetings();

  useEffect(() => {
    // Aggregate events from different sources
    const taskEvents = tasks.map(task => ({
      id: `task-${task.id}`,
      title: task.title,
      description: task.description,
      startTime: task.dueDate,
      endTime: task.dueDate,
      dealId: task.dealId,
      priority: task.priority || 'medium',
      notifications: ['1day'],
      type: 'task'
    }));

    const meetingEvents = meetings.map(meeting => ({
      id: `meeting-${meeting.id}`,
      title: meeting.topic,
      description: meeting.description,
      startTime: meeting.startTime,
      endTime: new Date(new Date(meeting.startTime).getTime() + meeting.duration * 60000).toISOString(),
      dealId: meeting.dealId,
      priority: 'medium',
      notifications: ['30min'],
      type: 'meeting'
    }));

    const dealEvents = deals.flatMap(deal => [
      {
        id: `deal-start-${deal.id}`,
        title: `${deal.name} Start`,
        description: `Start of ${deal.name} deal process`,
        startTime: deal.startDate,
        endTime: deal.startDate,
        dealId: deal.id,
        priority: 'medium',
        notifications: [],
        type: 'milestone'
      },
      {
        id: `deal-close-${deal.id}`,
        title: `${deal.name} Expected Close`,
        description: `Expected closing date for ${deal.name}`,
        startTime: deal.expectedCloseDate,
        endTime: deal.expectedCloseDate,
        dealId: deal.id,
        priority: 'high',
        notifications: ['1day'],
        type: 'milestone'
      }
    ]);

    const allEvents = [
      ...events.filter(e => !e.id.includes('task-') && !e.id.includes('meeting-') && !e.id.includes('deal-')),
      ...taskEvents,
      ...meetingEvents,
      ...dealEvents
    ].sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());

    setEvents(allEvents);
  }, [deals, tasks, meetings]);

  const addEvent = async (event: Omit<CalendarEvent, 'id'>) => {
    setLoading(true);
    try {
      const newEvent: CalendarEvent = {
        ...event,
        id: crypto.randomUUID(),
        type: 'custom'
      };
      setEvents([...events, newEvent]);
      
      // Schedule notifications
      event.notifications.forEach(time => {
        const notificationTime = calculateNotificationTime(new Date(event.startTime), time);
        scheduleNotification(newEvent, notificationTime);
      });
    } finally {
      setLoading(false);
    }
  };

  const updateEvent = async (eventId: string, updates: Partial<CalendarEvent>) => {
    setLoading(true);
    try {
      setEvents(events.map(event =>
        event.id === eventId ? { ...event, ...updates } : event
      ));
    } finally {
      setLoading(false);
    }
  };

  const deleteEvent = async (eventId: string) => {
    setLoading(true);
    try {
      if (!eventId.includes('-')) {
        setEvents(events.filter(event => event.id !== eventId));
      }
    } finally {
      setLoading(false);
    }
  };

  const calculateNotificationTime = (eventTime: Date, notification: string) => {
    const time = new Date(eventTime);
    switch (notification) {
      case '30min':
        time.setMinutes(time.getMinutes() - 30);
        break;
      case '1hour':
        time.setHours(time.getHours() - 1);
        break;
      case '1day':
        time.setDate(time.getDate() - 1);
        break;
    }
    return time;
  };

  const scheduleNotification = (event: CalendarEvent, time: Date) => {
    const timeUntilNotification = time.getTime() - Date.now();
    if (timeUntilNotification > 0) {
      setTimeout(() => {
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification(`Upcoming: ${event.title}`, {
            body: event.description,
            icon: '/calendar-icon.png'
          });
        }
      }, timeUntilNotification);
    }
  };

  return {
    events,
    loading,
    addEvent,
    updateEvent,
    deleteEvent
  };
}